import React, { useState } from 'react';
import { 
  User, Bell, Shield, CreditCard, Database, Globe, 
  Save, Eye, EyeOff, Key, Mail, Phone, Upload
} from 'lucide-react';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import { Select } from '../components/common/Select';
import { useToast } from '../components/common/Toast';
import { useAppData } from '../context/DataContext';

export const Settings: React.FC = () => {
  const { user, updateUser } = useAppData();
  const [activeTab, setActiveTab] = useState('profile');
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    // Profile
    name: user.name,
    email: user.email,
    phone: '+91-9876543210',
    company: 'Naad Music Group',
    bio: 'Music distribution and artist management professional',
    timezone: 'Asia/Kolkata',
    language: 'en',
    
    // Security
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    twoFactorEnabled: true,
    
    // Notifications
    emailNotifications: true,
    pushNotifications: true,
    weeklyReports: true,
    releaseAlerts: true,
    revenueAlerts: true,
    systemUpdates: false,
    
    // Distribution
    defaultGenre: 'Pop',
    autoDistribution: false,
    qualityCheck: true,
    metadataValidation: true,
    copyrightCheck: true,
    
    // Billing
    plan: user.plan,
    billingEmail: user.email,
    paymentMethod: 'card_ending_4242',
    autoRenew: true
  });

  const { addToast } = useToast();

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'distribution', label: 'Distribution', icon: Database },
    { id: 'billing', label: 'Billing', icon: CreditCard }
  ];

  const updateFormData = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = (section: string) => {
    if (section === 'profile') {
      updateUser({
        name: formData.name,
        email: formData.email,
      });
    }
    
    addToast({
      type: 'success',
      title: 'Settings Saved',
      message: `Your ${section} settings have been updated successfully.`
    });
  };

  const renderProfileSettings = () => (
    <div className="space-y-6">
      <Card>
        <h3 className="text-lg font-semibold text-white font-display mb-6">
          Personal Information
        </h3>
        
        <div className="space-y-6">
          {/* Avatar Upload */}
          <div className="flex items-center space-x-6">
            <img
              src={user.avatar}
              alt="Profile"
              className="w-20 h-20 rounded-full object-cover border-2 border-gold-500"
            />
            <div>
              <p className="text-white font-medium mb-2">Profile Picture</p>
              <Button variant="outline" size="sm" icon={Upload}>
                Upload New Photo
              </Button>
              <p className="text-xs text-gray-400 mt-1">
                JPG, PNG or GIF. Max size 2MB.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="Full Name"
              value={formData.name}
              onChange={(value) => updateFormData('name', value)}
              required
            />
            <Input
              label="Email Address"
              type="email"
              value={formData.email}
              onChange={(value) => updateFormData('email', value)}
              required
            />
            <Input
              label="Phone Number"
              type="tel"
              value={formData.phone}
              onChange={(value) => updateFormData('phone', value)}
            />
            <Input
              label="Company"
              value={formData.company}
              onChange={(value) => updateFormData('company', value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Bio
            </label>
            <textarea
              className="w-full px-3 py-2 bg-black-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gold-500 focus:border-transparent transition-all duration-200"
              rows={3}
              value={formData.bio}
              onChange={(e) => updateFormData('bio', e.target.value)}
              placeholder="Tell us about yourself..."
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Select
              label="Timezone"
              value={formData.timezone}
              onChange={(value) => updateFormData('timezone', value)}
              options={[
                { value: 'Asia/Kolkata', label: 'India (UTC+5:30)' },
                { value: 'America/New_York', label: 'Eastern Time (UTC-5)' },
                { value: 'America/Los_Angeles', label: 'Pacific Time (UTC-8)' },
                { value: 'Europe/London', label: 'GMT (UTC+0)' },
                { value: 'Asia/Tokyo', label: 'JST (UTC+9)' }
              ]}
            />
            <Select
              label="Language"
              value={formData.language}
              onChange={(value) => updateFormData('language', value)}
              options={[
                { value: 'en', label: 'English' },
                { value: 'hi', label: 'Hindi' },
                { value: 'es', label: 'Spanish' },
                { value: 'fr', label: 'French' },
                { value: 'de', label: 'German' }
              ]}
            />
          </div>

          <div className="flex justify-end">
            <Button onClick={() => handleSave('profile')} icon={Save}>
              Save Changes
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );

  const renderSecuritySettings = () => (
    <div className="space-y-6">
      <Card>
        <h3 className="text-lg font-semibold text-white font-display mb-6">
          Change Password
        </h3>
        
        <div className="space-y-4">
          <Input
            label="Current Password"
            type={showPassword ? 'text' : 'password'}
            value={formData.currentPassword}
            onChange={(value) => updateFormData('currentPassword', value)}
            icon={showPassword ? EyeOff : Eye}
            iconPosition="right"
          />
          <Input
            label="New Password"
            type={showPassword ? 'text' : 'password'}
            value={formData.newPassword}
            onChange={(value) => updateFormData('newPassword', value)}
          />
          <Input
            label="Confirm New Password"
            type={showPassword ? 'text' : 'password'}
            value={formData.confirmPassword}
            onChange={(value) => updateFormData('confirmPassword', value)}
          />
          
          <div className="flex items-center justify-between">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={showPassword}
                onChange={(e) => setShowPassword(e.target.checked)}
                className="rounded border-gray-600 bg-black-700 text-gold-500 focus:ring-gold-500"
              />
              <span className="text-sm text-gray-300">Show passwords</span>
            </label>
            
            <Button onClick={() => handleSave('password')}>
              Update Password
            </Button>
          </div>
        </div>
      </Card>

      <Card>
        <h3 className="text-lg font-semibold text-white font-display mb-6">
          Two-Factor Authentication
        </h3>
        
        <div className="flex items-center justify-between">
          <div>
            <p className="text-white font-medium mb-1">
              Two-Factor Authentication
            </p>
            <p className="text-sm text-gray-400">
              Add an extra layer of security to your account
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={formData.twoFactorEnabled}
              onChange={(e) => updateFormData('twoFactorEnabled', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-gold-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gold-500"></div>
          </label>
        </div>

        {formData.twoFactorEnabled && (
          <div className="mt-6 p-4 bg-gold-500/10 border border-gold-500/20 rounded-lg">
            <div className="flex items-center space-x-3">
              <Key className="w-5 h-5 text-gold-400" />
              <div>
                <p className="text-gold-400 font-medium">2FA is enabled</p>
                <p className="text-sm text-gray-300">
                  Your account is protected with two-factor authentication
                </p>
              </div>
            </div>
          </div>
        )}
      </Card>

      <Card>
        <h3 className="text-lg font-semibold text-white font-display mb-6">
          Login Sessions
        </h3>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg">
            <div>
              <p className="text-white font-medium">Current Session</p>
              <p className="text-sm text-gray-400">Chrome on Windows • India</p>
              <p className="text-xs text-gray-500">Active now</p>
            </div>
            <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">
              Current
            </span>
          </div>
          
          <div className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg">
            <div>
              <p className="text-white font-medium">Mobile App</p>
              <p className="text-sm text-gray-400">iPhone • India</p>
              <p className="text-xs text-gray-500">2 hours ago</p>
            </div>
            <Button variant="outline" size="sm">
              Revoke
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );

  const renderNotificationSettings = () => (
    <div className="space-y-6">
      <Card>
        <h3 className="text-lg font-semibold text-white font-display mb-6">
          Email Notifications
        </h3>
        
        <div className="space-y-4">
          {[
            { key: 'emailNotifications', label: 'General Email Notifications', desc: 'Receive email updates about your account' },
            { key: 'weeklyReports', label: 'Weekly Performance Reports', desc: 'Get weekly summaries of your music performance' },
            { key: 'releaseAlerts', label: 'Release Status Updates', desc: 'Notifications when your music goes live on platforms' },
            { key: 'revenueAlerts', label: 'Revenue Milestones', desc: 'Alerts when you hit revenue milestones' },
            { key: 'systemUpdates', label: 'System Updates', desc: 'Important updates about the platform' }
          ].map((item) => (
            <div key={item.key} className="flex items-center justify-between">
              <div>
                <p className="text-white font-medium">{item.label}</p>
                <p className="text-sm text-gray-400">{item.desc}</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData[item.key as keyof typeof formData] as boolean}
                  onChange={(e) => updateFormData(item.key, e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-gold-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gold-500"></div>
              </label>
            </div>
          ))}
        </div>

        <div className="flex justify-end mt-6">
          <Button onClick={() => handleSave('notifications')} icon={Save}>
            Save Preferences
          </Button>
        </div>
      </Card>
    </div>
  );

  const renderDistributionSettings = () => (
    <div className="space-y-6">
      <Card>
        <h3 className="text-lg font-semibold text-white font-display mb-6">
          Distribution Preferences
        </h3>
        
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Select
              label="Default Genre"
              value={formData.defaultGenre}
              onChange={(value) => updateFormData('defaultGenre', value)}
              options={[
                { value: 'Pop', label: 'Pop' },
                { value: 'Rock', label: 'Rock' },
                { value: 'Hip Hop', label: 'Hip Hop' },
                { value: 'Electronic', label: 'Electronic' },
                { value: 'Classical', label: 'Classical' },
                { value: 'Bollywood', label: 'Bollywood' }
              ]}
            />
          </div>

          <div className="space-y-4">
            {[
              { key: 'autoDistribution', label: 'Auto-Distribution', desc: 'Automatically distribute to all enabled stores' },
              { key: 'qualityCheck', label: 'Quality Check', desc: 'Perform automated quality checks on uploaded audio' },
              { key: 'metadataValidation', label: 'Metadata Validation', desc: 'Validate metadata before distribution' },
              { key: 'copyrightCheck', label: 'Copyright Check', desc: 'Run copyright checks on uploaded content' }
            ].map((item) => (
              <div key={item.key} className="flex items-center justify-between">
                <div>
                  <p className="text-white font-medium">{item.label}</p>
                  <p className="text-sm text-gray-400">{item.desc}</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData[item.key as keyof typeof formData] as boolean}
                    onChange={(e) => updateFormData(item.key, e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-gold-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gold-500"></div>
                </label>
              </div>
            ))}
          </div>

          <div className="flex justify-end">
            <Button onClick={() => handleSave('distribution')} icon={Save}>
              Save Settings
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );

  const renderBillingSettings = () => (
    <div className="space-y-6">
      <Card>
        <h3 className="text-lg font-semibold text-white font-display mb-6">
          Current Plan
        </h3>
        
        <div className="flex items-center justify-between p-6 bg-gold-500/10 border border-gold-500/20 rounded-lg">
          <div>
            <p className="text-gold-400 font-bold text-xl capitalize">{formData.plan} Plan</p>
            <p className="text-gray-300">Unlimited distributions • Advanced analytics</p>
            <p className="text-sm text-gray-400 mt-1">Next billing: December 1, 2024</p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-white">$29/mo</p>
            <Button variant="outline" size="sm" className="mt-2">
              Change Plan
            </Button>
          </div>
        </div>
      </Card>

      <Card>
        <h3 className="text-lg font-semibold text-white font-display mb-6">
          Payment Method
        </h3>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-6 bg-blue-600 rounded flex items-center justify-center text-white text-xs font-bold">
                VISA
              </div>
              <div>
                <p className="text-white font-medium">•••• •••• •••• 4242</p>
                <p className="text-sm text-gray-400">Expires 12/2027</p>
              </div>
            </div>
            <div className="flex space-x-2">
              <Button variant="outline" size="sm">Edit</Button>
              <Button variant="outline" size="sm">Remove</Button>
            </div>
          </div>
          
          <Button variant="outline" icon={CreditCard}>
            Add Payment Method
          </Button>
        </div>
      </Card>

      <Card>
        <h3 className="text-lg font-semibold text-white font-display mb-6">
          Billing History
        </h3>
        
        <div className="space-y-3">
          {[
            { date: 'Nov 1, 2024', amount: '$29.00', status: 'Paid', invoice: 'INV-001' },
            { date: 'Oct 1, 2024', amount: '$29.00', status: 'Paid', invoice: 'INV-002' },
            { date: 'Sep 1, 2024', amount: '$29.00', status: 'Paid', invoice: 'INV-003' }
          ].map((item, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
              <div>
                <p className="text-white font-medium">{item.date}</p>
                <p className="text-sm text-gray-400">{item.invoice}</p>
              </div>
              <div className="flex items-center space-x-4">
                <span className="text-white font-medium">{item.amount}</span>
                <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">
                  {item.status}
                </span>
                <Button variant="ghost" size="sm">
                  Download
                </Button>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'profile': return renderProfileSettings();
      case 'security': return renderSecuritySettings();
      case 'notifications': return renderNotificationSettings();
      case 'distribution': return renderDistributionSettings();
      case 'billing': return renderBillingSettings();
      default: return renderProfileSettings();
    }
  };

  return (
    <div className="flex gap-6">
      {/* Sidebar */}
      <div className="w-64 flex-shrink-0">
        <Card padding="none" className="sticky top-6">
          <div className="p-4 border-b border-gray-700">
            <h2 className="text-lg font-semibold text-white font-display">Settings</h2>
          </div>
          <nav className="p-2">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left
                    transition-colors duration-200
                    ${activeTab === tab.id 
                      ? 'gold-gradient text-black font-medium' 
                      : 'text-gray-300 hover:text-white hover:bg-gray-800'
                    }
                  `}
                >
                  <Icon className="w-5 h-5" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </Card>
      </div>

      {/* Content */}
      <div className="flex-1">
        {renderContent()}
      </div>
    </div>
  );
};